<template>
  <div class="app-root">
    <!-- ═══════════════════════════════════════════════
         SÉLECTEUR DE VUE (demo routing)
    ════════════════════════════════════════════════ -->
    <!-- Masqué en mode borne : la sortie n'est possible que via le bouton protégé par PIN -->
    <div class="view-switcher" v-if="currentView !== 'kiosk'">
      <button
        v-for="v in views"
        :key="v.key"
        @click="v.key === 'kiosk' ? enterKiosk() : (currentView = v.key)"
        :class="['view-tab', { active: currentView === v.key }]"
      >
        {{ v.label }}
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════
         VUE 1 — DASHBOARD SUPERVISEUR
    ════════════════════════════════════════════════ -->
    <div v-if="currentView === 'supervisor'" class="supervisor-dashboard">
      <dashboard-context-header title="ANIMATION REPAS" @context-change="onContextChange">
        <template #extra>
          <div class="vote-summary-bar" v-if="childVotes.length > 0">
            <span class="vote-summary-label">🗳️ Votes enfants : {{ childVotes.length }}</span>
            <span class="vote-pill pill-green">😄 {{ childVoteSummary.liked }}</span>
            <span class="vote-pill pill-orange">😐 {{ childVoteSummary.medium }}</span>
            <span class="vote-pill pill-red">😞 {{ childVoteSummary.disliked }}</span>
            <span class="vote-pill pill-blue">🍽️ Assez : {{ childVoteSummary.enoughYes }}</span>
            <span class="vote-pill pill-red">🍽️ Pas assez : {{ childVoteSummary.enoughNo }}</span>
          </div>
          <div v-else class="no-votes-message">
            <span class="no-votes-icon">📊</span>
            <span class="no-votes-text">
              Aucun vote enfant pour {{ formatDisplayDate() }} - {{ getServiceLabel() }}
            </span>
          </div>
        </template>
      </dashboard-context-header>

      <!-- Cartes principales -->
      <div class="cards-grid">
        <!-- Card 1 -->
        <div class="card card-featured" @click="showGlobalFeedback = true">
          <div class="card-header">
            <h3 class="card-title">😊 Appréciation globale</h3>
            <span class="badge badge-green">{{ getRatingLabel(globalRating) }}</span>
          </div>
          <p class="card-description">Donner votre évaluation du repas</p>
          <button class="card-link">Évaluer →</button>
        </div>

        <!-- Card 2 -->
        <div class="card" @click="showDetailedFeedback = true">
          <div class="card-header">
            <h3 class="card-title">🍽️ Détail par plat</h3>
            <span class="badge badge-blue">{{ dishes.length }} plats</span>
          </div>
          <p class="card-description">Noter chaque plat individuellement</p>
          <button class="card-link">Noter →</button>
        </div>

        <!-- Card 3 -->
        <div class="card" @click="showContextForm = true">
          <div class="card-header">
            <h3 class="card-title">🌤️ Contexte du jour</h3>
          </div>
          <p class="card-description">Météo, ambiance et observations</p>
          <button class="card-link">Renseigner →</button>
        </div>

        <!-- Card 4 -->
        <div v-if="featureCommunications" class="card" @click="showCommunications = true">
          <div class="card-header">
            <h3 class="card-title">📢 Communications</h3>
          </div>
          <p class="card-description">Messages et alertes importantes</p>
          <button class="card-link">Voir →</button>
        </div>

        <!-- Card 5 -->
        <div v-if="featureWaste" class="card card-highlight" @click="showWasteToKids = true">
          <div class="card-header">
            <h3 class="card-title">👀 Montrer aux enfants</h3>
          </div>
          <p class="card-description">Visualisation ludique du gaspillage</p>
          <button class="card-link">Afficher →</button>
        </div>

        <!-- Card 6 -->
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">💬 Ajouter</h3>
          </div>
          <div class="action-buttons">
            <button @click="addComment" class="action-btn">
              <span class="action-icon">💬</span>
              <span>Commentaire</span>
            </button>
            <button @click="takePhoto" class="action-btn">
              <span class="action-icon">📸</span>
              <span>Photo</span>
            </button>
          </div>
        </div>

        <!-- Card 7 — NOUVELLE : Résultats votes enfants (MVP 5 & 6) -->
        <div v-if="featureVotes" class="card card-votes" @click="showVoteResults = true">
          <div class="card-header">
            <h3 class="card-title">🗳️ Votes enfants</h3>
            <span class="badge badge-purple">{{ childVotes.length }} votes</span>
          </div>
          <p class="card-description">Résultats du vote numérique en temps réel</p>
          <div class="vote-mini-bars" v-if="childVotes.length > 0">
            <div class="mini-bar-row">
              <span>😄</span>
              <div class="mini-bar-track">
                <div class="mini-bar-fill fill-green" :style="{ width: pct(childVoteSummary.liked) + '%' }"></div>
              </div>
              <span class="mini-bar-val">{{ pct(childVoteSummary.liked) }}%</span>
            </div>
            <div class="mini-bar-row">
              <span>😐</span>
              <div class="mini-bar-track">
                <div class="mini-bar-fill fill-orange" :style="{ width: pct(childVoteSummary.medium) + '%' }"></div>
              </div>
              <span class="mini-bar-val">{{ pct(childVoteSummary.medium) }}%</span>
            </div>
            <div class="mini-bar-row">
              <span>😞</span>
              <div class="mini-bar-track">
                <div class="mini-bar-fill fill-red" :style="{ width: pct(childVoteSummary.disliked) + '%' }"></div>
              </div>
              <span class="mini-bar-val">{{ pct(childVoteSummary.disliked) }}%</span>
            </div>
          </div>
          <p v-else class="card-description" style="font-style: italic">Aucun vote pour le moment</p>
          <button class="card-link">Consulter →</button>
        </div>

        <!-- Card 8 — NOUVELLE : Lancer la borne vote enfant -->
        <div v-if="featureVotes" class="card card-kiosk" @click="enterKiosk">
          <div class="card-header">
            <h3 class="card-title">📲 Borne de vote</h3>
          </div>
          <p class="card-description">Ouvrir l'écran de vote pour les enfants (tablette dédiée)</p>
          <button class="card-link">Lancer la borne →</button>
        </div>
      </div>

      <!-- ─── MODALS SUPERVISEUR ─────────────────────────── -->

      <!-- Modal: Appréciation globale -->
      <div v-if="showGlobalFeedback" class="modal-overlay" @click="showGlobalFeedback = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showGlobalFeedback = false">✕</button>
          <h2 class="modal-title">😊 Appréciation globale</h2>
          <p class="modal-subtitle">Comment s'est passé le repas ?</p>
          <div class="rating-selector">
            <button
              v-for="(emoji, idx) in emojis"
              :key="idx"
              @click="globalRating = idx + 1"
              :class="{ active: globalRating === idx + 1 }"
              class="rating-btn"
            >
              <span class="rating-emoji">{{ emoji }}</span>
              <span class="rating-label">{{ getRatingLabelByIndex(idx) }}</span>
            </button>
          </div>
          <button v-if="globalRating" @click="saveGlobalRating" class="btn btn-primary">Enregistrer</button>
        </div>
      </div>

      <!-- Modal: Détail par plat -->
      <div v-if="showDetailedFeedback" class="modal-overlay" @click="showDetailedFeedback = false">
        <div class="modal-content modal-large" @click.stop>
          <button class="modal-close" @click="showDetailedFeedback = false">✕</button>
          <h2 class="modal-title">🍽️ DÉTAIL PAR PLAT</h2>
          <div class="dishes-list">
            <div v-for="(dish, idx) in dishes" :key="idx" class="dish-card">
              <div class="flex justify-between items-start mb-3">
                <div>
                  <h4 class="dish-name">{{ dish.name }}</h4>
                  <p class="text-xs text-gray-500">{{ dish.category }}</p>
                </div>
              </div>
              <div class="mb-3">
                <p class="text-xs text-gray-600 mb-1">Appréciation</p>
                <div class="dish-ratings">
                  <button
                    v-for="(emoji, i) in emojis"
                    :key="i"
                    @click="updateDishRating(idx, i + 1)"
                    :class="['rating-mini-btn', { active: dish.rating === i + 1 }]"
                  >
                    {{ emoji }}
                  </button>
                  <span class="rating-percent">{{ dish.rating ? getRatingLabel(dish.rating) : "" }}</span>
                </div>
              </div>
              <div v-if="dish.comment" class="dish-comment">💬 "{{ dish.comment }}"</div>
              <button @click="editDishComment(idx)" class="text-xs text-blue-600 mt-2 hover:underline">
                {{ dish.comment ? "Modifier" : "Ajouter" }} un commentaire
              </button>
            </div>
          </div>
          <button @click="saveDishRatings" class="btn btn-primary">Terminé</button>
        </div>
      </div>

      <!-- Modal: Contexte -->
      <div v-if="showContextForm" class="modal-overlay" @click="showContextForm = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showContextForm = false">✕</button>
          <h2 class="modal-title">🌤️ Contexte du jour</h2>
          <div class="detail-section">
            <h3 class="section-title">Météo</h3>
            <div class="weather-selector">
              <button
                v-for="w in weatherOptions"
                :key="w.key"
                @click="weather = w.key"
                :class="{ active: weather === w.key }"
                class="weather-btn"
              >
                <span class="weather-icon">{{ w.icon }}</span>
                <span class="weather-label">{{ w.label }}</span>
              </button>
            </div>
          </div>
          <div class="detail-section">
            <h3 class="section-title">Température</h3>
            <div class="temperature-input">
              <input v-model.number="temperature" type="number" class="input-field" placeholder="22" />
              <span class="temperature-unit">°C</span>
            </div>
          </div>
          <div class="detail-section">
            <h3 class="section-title">Niveau sonore</h3>
            <div class="noise-selector">
              <button
                v-for="n in noiseOptions"
                :key="n.key"
                @click="noiseLevel = n.key"
                :class="{ active: noiseLevel === n.key }"
                class="noise-btn"
              >
                <span class="noise-icon">{{ n.icon }}</span>
                <span class="noise-label">{{ n.label }}</span>
              </button>
            </div>
          </div>
          <button @click="saveContext" class="btn btn-primary">Enregistrer</button>
        </div>
      </div>

      <!-- Modal: Communications -->
      <div v-if="showCommunications && featureCommunications" class="modal-overlay" @click="showCommunications = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showCommunications = false">✕</button>
          <communication-panel2
            :canteen-id="canteenId"
            :date="selectedDate"
            :service-id="selectedService"
            :variant="effectifVariant"
          />
        </div>
      </div>

      <!-- Modal: Résultats votes enfants (MVP 5 & 6) -->
      <div v-if="showVoteResults && featureVotes" class="modal-overlay" @click="showVoteResults = false">
        <div class="modal-content modal-large" @click.stop>
          <button class="modal-close" @click="showVoteResults = false">✕</button>
          <h2 class="modal-title">🗳️ Résultats — Votes enfants</h2>
          <p class="modal-subtitle">{{ childVotes.length }} vote(s) enregistré(s) ce service</p>

          <!-- Section Q1 : Repas aimé -->
          <div class="result-section">
            <h3 class="section-title">😋 As-tu aimé le repas ?</h3>
            <div class="result-bars">
              <div class="result-row">
                <span class="result-label">😄 Oui</span>
                <div class="result-track">
                  <div class="result-fill fill-green" :style="{ width: pct(childVoteSummary.liked) + '%' }"></div>
                </div>
                <span class="result-count">{{ childVoteSummary.liked }} ({{ pct(childVoteSummary.liked) }}%)</span>
              </div>
              <div class="result-row">
                <span class="result-label">😐 Moyen</span>
                <div class="result-track">
                  <div class="result-fill fill-orange" :style="{ width: pct(childVoteSummary.medium) + '%' }"></div>
                </div>
                <span class="result-count">{{ childVoteSummary.medium }} ({{ pct(childVoteSummary.medium) }}%)</span>
              </div>
              <div class="result-row">
                <span class="result-label">😞 Non</span>
                <div class="result-track">
                  <div class="result-fill fill-red" :style="{ width: pct(childVoteSummary.disliked) + '%' }"></div>
                </div>
                <span class="result-count">
                  {{ childVoteSummary.disliked }} ({{ pct(childVoteSummary.disliked) }}%)
                </span>
              </div>
            </div>
          </div>

          <!-- Section Q2 : Assez mangé -->
          <div class="result-section">
            <h3 class="section-title">🍽️ As-tu assez mangé ?</h3>
            <div class="result-bars">
              <div class="result-row">
                <span class="result-label">✅ Oui</span>
                <div class="result-track">
                  <div
                    class="result-fill fill-green"
                    :style="{
                      width:
                        pctOf(childVoteSummary.enoughYes, childVoteSummary.enoughYes + childVoteSummary.enoughNo) + '%',
                    }"
                  ></div>
                </div>
                <span class="result-count">{{ childVoteSummary.enoughYes }}</span>
              </div>
              <div class="result-row">
                <span class="result-label">❌ Non</span>
                <div class="result-track">
                  <div
                    class="result-fill fill-red"
                    :style="{
                      width:
                        pctOf(childVoteSummary.enoughNo, childVoteSummary.enoughYes + childVoteSummary.enoughNo) + '%',
                    }"
                  ></div>
                </div>
                <span class="result-count">{{ childVoteSummary.enoughNo }}</span>
              </div>
            </div>
          </div>

          <!-- Section Q3 conditionnelle : Pourquoi pas assez ? (MVP 6) -->
          <div class="result-section" v-if="childVoteSummary.enoughNo > 0">
            <h3 class="section-title">
              🔍 Pourquoi pas assez ?
              <span class="badge badge-orange">{{ childVoteSummary.enoughNo }} réponses</span>
            </h3>
            <div class="result-bars">
              <div class="result-row" v-for="reason in notEnoughReasons" :key="reason.key">
                <span class="result-label">{{ reason.icon }} {{ reason.label }}</span>
                <div class="result-track">
                  <div
                    class="result-fill fill-orange"
                    :style="{
                      width: pctOf(childVoteSummary.reasons[reason.key] || 0, childVoteSummary.enoughNo) + '%',
                    }"
                  ></div>
                </div>
                <span class="result-count">{{ childVoteSummary.reasons[reason.key] || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Section Q4 : Plats problématiques -->
          <div
            class="result-section"
            v-if="
              childVoteSummary.dishTotals &&
                Object.keys(childVoteSummary.dishTotals).some((k) => childVoteSummary.dishTotals[k] > 0)
            "
          >
            <h3 class="section-title">
              🍽️ Plats qui ont posé problème
              <span class="badge badge-orange">signalements</span>
            </h3>
            <div class="result-bars">
              <div class="result-row" v-for="dish in dishCategories" :key="dish.key">
                <span class="result-label">{{ dish.emoji }} {{ dish.label }}</span>
                <div class="result-track">
                  <div
                    class="result-fill fill-orange"
                    :style="{ width: pctOf(childVoteSummary.dishTotals[dish.key] || 0, childVotes.length) + '%' }"
                  ></div>
                </div>
                <span class="result-count">{{ childVoteSummary.dishTotals[dish.key] || 0 }}</span>
              </div>
            </div>
          </div>

          <button @click="showVoteResults = false" class="btn btn-primary" style="margin-top: 1rem">Fermer</button>
        </div>
      </div>

      <!-- Fullscreen modal -->
      <div v-if="showWasteToKids && featureWaste" class="fullscreen-modal">
        <button @click="showWasteToKids = false" class="fullscreen-close">✕ Fermer</button>
        <waste-input-panel
          :canteen-id="canteenId"
          :meal-date="selectedDate"
          :service-id="selectedService ? Number(selectedService) : null"
          variant="simple"
        />
      </div>
    </div>
    <!-- /supervisor -->

    <!-- ═══════════════════════════════════════════════
         VUE 2 — BORNE VOTE ENFANT (Consumer / Kiosk)
         EPIC MVP 5 & 6
    ════════════════════════════════════════════════ -->
    <div v-if="currentView === 'kiosk'" class="kiosk-root">
      <!-- Sortie de borne discrète et protégée par PIN (durcissement kiosque) -->
      <button class="kiosk-exit" @click="requestKioskExit" aria-label="Quitter le mode borne">⚙</button>

      <!-- Dialogue de saisie du PIN de sortie -->
      <div v-if="showKioskExitDialog" class="kiosk-pin-overlay" @click.self="cancelKioskExit">
        <div class="kiosk-pin-dialog" role="dialog" aria-modal="true" aria-label="Code de sortie de la borne">
          <h2 class="kiosk-pin-title">Sortie de la borne</h2>
          <p class="kiosk-pin-sub">Saisis le code pour revenir au tableau de bord superviseur.</p>
          <input
            ref="kioskPinField"
            v-model="kioskPinInput"
            type="password"
            inputmode="numeric"
            autocomplete="off"
            class="kiosk-pin-input"
            :class="{ 'kiosk-pin-input--error': kioskPinError }"
            maxlength="8"
            placeholder="••••"
            aria-label="Code PIN de sortie"
            @keyup.enter="submitKioskPin"
          />
          <p v-if="kioskPinError" class="kiosk-pin-error" role="alert">Code incorrect, réessaie.</p>
          <div class="kiosk-pin-actions">
            <button class="kiosk-pin-cancel" @click="cancelKioskExit">Annuler</button>
            <button class="kiosk-pin-submit" :disabled="kioskPinChecking" @click="submitKioskPin">
              {{ kioskPinChecking ? "…" : "Valider" }}
            </button>
          </div>
        </div>
      </div>

      <!-- ── ÉTAPE 0 : Écran d'accueil ────────────────── -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="kioskStep === 0" key="step0" class="kiosk-screen kiosk-welcome">
          <div class="kiosk-mascot">🧒</div>
          <h1 class="kiosk-welcome-title">À toi de voter !</h1>
          <p class="kiosk-welcome-sub">Dis-nous ce que tu as pensé du repas</p>
          <button class="kiosk-start-btn" @click="startVote">Commencer 👆</button>
        </div>

        <!-- ── ÉTAPE 1 : As-tu aimé le repas ? ─────────── -->
        <div v-else-if="kioskStep === 1" key="step1" class="kiosk-screen">
          <div class="kiosk-progress">
            <div class="kiosk-progress-dot active"></div>
            <div class="kiosk-progress-dot"></div>
          </div>
          <h2 class="kiosk-question">
            As-tu aimé
            <br />
            le repas ?
          </h2>
          <div class="kiosk-choices kiosk-choices-3">
            <button class="kiosk-choice-btn choice-green" @click="selectMealRating('liked')">
              <span class="kiosk-smiley">😄</span>
              <span class="kiosk-choice-label">Oui !</span>
            </button>
            <button class="kiosk-choice-btn choice-orange" @click="selectMealRating('medium')">
              <span class="kiosk-smiley">😐</span>
              <span class="kiosk-choice-label">Moyen</span>
            </button>
            <button class="kiosk-choice-btn choice-red" @click="selectMealRating('disliked')">
              <span class="kiosk-smiley">😞</span>
              <span class="kiosk-choice-label">Non</span>
            </button>
          </div>
        </div>

        <!-- ── ÉTAPE 2 : As-tu assez mangé ? ────────────── -->
        <div v-else-if="kioskStep === 2" key="step2" class="kiosk-screen">
          <div class="kiosk-progress">
            <div class="kiosk-progress-dot active"></div>
            <div class="kiosk-progress-dot active"></div>
          </div>
          <h2 class="kiosk-question">
            As-tu assez
            <br />
            mangé ?
          </h2>
          <div class="kiosk-choices kiosk-choices-2">
            <button class="kiosk-choice-btn choice-green" @click="selectEnough('yes')">
              <span class="kiosk-smiley">✅</span>
              <span class="kiosk-choice-label">Oui !</span>
            </button>
            <button class="kiosk-choice-btn choice-red" @click="selectEnough('no')">
              <span class="kiosk-smiley">❌</span>
              <span class="kiosk-choice-label">Non</span>
            </button>
          </div>
        </div>

        <!-- ── ÉTAPE 3 (conditionnel) : Pourquoi ? ── -->
        <div v-else-if="kioskStep === 3" key="step3" class="kiosk-screen">
          <div class="kiosk-progress">
            <div v-for="i in maxSteps" :key="i" class="kiosk-progress-dot" :class="{ active: i <= 3 }"></div>
          </div>
          <h2 class="kiosk-question">
            Pourquoi tu
            <br />
            n'as pas assez mangé ?
          </h2>
          <div class="kiosk-choices kiosk-choices-col">
            <button
              v-for="reason in notEnoughReasons"
              :key="reason.key"
              class="kiosk-reason-btn"
              @click="selectReason(reason.key)"
            >
              <span class="kiosk-reason-icon">{{ reason.icon }}</span>
              <span class="kiosk-reason-label">{{ reason.label }}</span>
            </button>
          </div>
        </div>

        <!-- ── ÉTAPE 4 : Quel plat a posé problème ? (multi-choix) ── -->
        <div v-else-if="kioskStep === 4" key="step4" class="kiosk-screen">
          <div class="kiosk-progress">
            <div v-for="i in maxSteps" :key="i" class="kiosk-progress-dot" :class="{ active: i <= 4 }"></div>
          </div>
          <h2 class="kiosk-question kiosk-question-sm">
            C'était lequel
            <br />
            le problème ?
          </h2>
          <p class="kiosk-dish-hint">Tu peux en choisir plusieurs ! 👇</p>
          <div class="kiosk-dish-grid">
            <button
              v-for="dish in dishCategories"
              :key="dish.key"
              class="kiosk-dish-btn"
              :class="{ 'dish-selected': currentVote.problematicDishes.indexOf(dish.key) !== -1 }"
              @click="toggleDish(dish.key)"
            >
              <span class="kiosk-dish-plate">
                <span class="kiosk-dish-emoji">{{ dish.emoji }}</span>
                <span v-if="currentVote.problematicDishes.indexOf(dish.key) !== -1" class="dish-check">✓</span>
              </span>
              <span class="kiosk-dish-name">{{ dish.label }}</span>
              <span class="kiosk-dish-sub">{{ dish.sub }}</span>
            </button>
          </div>
          <button
            class="kiosk-validate-btn"
            :class="{ 'kiosk-validate-ready': currentVote.problematicDishes.length > 0 }"
            @click="validateDishes"
          >
            <span v-if="currentVote.problematicDishes.length === 0">Passer ➜</span>
            <span v-else>C'est noté ! ✅</span>
          </button>
        </div>

        <!-- ── ÉTAPE 5 : Confirmation ──────────────────── -->
        <div v-else-if="kioskStep === 5" key="step5" class="kiosk-screen kiosk-confirm">
          <div class="kiosk-confirm-icon">🎉</div>
          <h2 class="kiosk-confirm-title">Merci !</h2>
          <p class="kiosk-confirm-sub">Ton avis a bien été enregistré</p>
          <div class="kiosk-confirm-recap">
            <span v-if="currentVote.mealRating === 'liked'">😄 Tu as aimé le repas</span>
            <span v-if="currentVote.mealRating === 'medium'">😐 Le repas était moyen</span>
            <span v-if="currentVote.mealRating === 'disliked'">😞 Tu n'as pas aimé le repas</span>
            <br />
            <span v-if="currentVote.enough === 'yes'">✅ Tu as assez mangé</span>
            <span v-if="currentVote.enough === 'no'">❌ Tu n'as pas assez mangé</span>
            <template v-if="currentVote.problematicDishes && currentVote.problematicDishes.length > 0">
              <br />
              <span>
                🍽️ Problème sur :
                <span v-for="(dk, i) in currentVote.problematicDishes" :key="dk">
                  {{ getDishLabel(dk) }}
                  <span v-if="i < currentVote.problematicDishes.length - 1">,</span>
                </span>
              </span>
            </template>
          </div>
          <button class="kiosk-next-btn" @click="resetKiosk">Suivant →</button>
        </div>
      </transition>
    </div>
    <!-- /kiosk -->
  </div>
</template>

<script>
import WasteInputPanel from "@/components/WasteInputPanel.vue"
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"
import CommunicationPanel2 from "@/components/CommunicationPanel2.vue"

export default {
  name: "SupervisorDashboard5",
  components: {
    WasteInputPanel,
    DashboardContextHeader,
    CommunicationPanel2,
  },

  data() {
    return {
      // ── Navigation ──────────────────────────────────
      currentView: "supervisor",
      views: [
        { key: "supervisor", label: "👩‍💼 Superviseur" },
        { key: "kiosk", label: "📲 Borne enfant" },
      ],

      // ── API Integration ──────────────────────────────
      canteenId: null,
      apiBaseUrl: "/api/v1",
      isLoadingStats: false,
      lastStatsUpdate: null,
      statsInterval: null,

      // ── Date and Service Selection ────────────────────
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
      availableServices: [],
      effectifVariant: "SIMPLE",

      // ── Supervisor modals ────────────────────────────
      showGlobalFeedback: false,
      showDetailedFeedback: false,
      showContextForm: false,
      showCommunications: false,
      showWasteToKids: false,
      showVoteResults: false,
      featureCommunications: true,
      featureWaste: true,
      featureVotes: true,
      featureSupervisorFeedback: true,
      featureSurplus: true,

      // ── Supervisor data ──────────────────────────────
      emojis: ["😞", "😕", "😐", "😊", "😍"],
      globalRating: null,
      noiseLevel: 3,
      noiseOptions: [
        { key: 1, icon: "😊", label: "Calme" },
        { key: 2, icon: "🙂", label: "Plutôt calme" },
        { key: 3, icon: "😐", label: "Normal" },
        { key: 4, icon: "😟", label: "Bruyant" },
        { key: 5, icon: "😱", label: "Très bruyant" },
      ],
      temperature: 22,
      weather: "sunny",
      weatherOptions: [
        { key: "sunny", icon: "☀️", label: "Ensoleillé" },
        { key: "cloudy", icon: "☁️", label: "Nuageux" },
        { key: "rainy", icon: "🌧️", label: "Pluvieux" },
      ],
      dishes: [],

      // ── Supervisor Feedback API ───────────────────────
      supervisorFeedbackId: null,
      isSavingFeedback: false,
      supervisorComment: "",
      supervisorPhotoFile: null,

      // ── Kiosk (MVP 5 & 6) ────────────────────────────
      // 0=accueil 1=Q1-repas 2=Q2-assez 3=Q3-pourquoi 4=Q4-plat 5=confirm
      kioskStep: 0,
      maxSteps: 4,
      currentVote: {
        mealRating: null,
        enough: null,
        reason: null,
        problematicDishes: [],
      },
      childVotes: [],

      // ── Sortie borne protégée par PIN ─────────────────
      hasKioskExitPin: false,
      showKioskExitDialog: false,
      kioskPinInput: "",
      kioskPinError: false,
      kioskPinChecking: false,

      notEnoughReasons: [
        { key: "quantity", icon: "🥄", label: "Pas assez de quantité" },
        { key: "taste", icon: "🤢", label: "Je n'ai pas aimé" },
        { key: "other", icon: "🤔", label: "Autre raison" },
      ],

      menuItems: [],
    }
  },

  computed: {
    dishCategories() {
      const VOTE_MAP = {
        entree: { key: "starter", emoji: "🥗", defaultLabel: "L'entrée", defaultSub: "salade, crudités…" },
        plat: { key: "main", emoji: "🍗", defaultLabel: "Le plat chaud", defaultSub: "viande, poisson…" },
        accompagnement: { key: "side", emoji: "🥦", defaultLabel: "L'accomp.", defaultSub: "légumes, féculents…" },
        produit_laitier: { key: "dairy", emoji: "🧀", defaultLabel: "Produit laitier", defaultSub: "fromage, yaourt…" },
        dessert: { key: "dessert", emoji: "🍮", defaultLabel: "Le dessert", defaultSub: "fruit, gâteau…" },
      }
      const defaults = Object.values(VOTE_MAP).map((m) => ({
        key: m.key,
        emoji: m.emoji,
        label: m.defaultLabel,
        sub: m.defaultSub,
      }))
      if (!this.menuItems || this.menuItems.length === 0) return defaults

      // Construire la liste à partir des plats importés
      const result = []
      const vegeItem = this.menuItems.find((i) => i.category === "sans_viande")
      Object.entries(VOTE_MAP).forEach(([apiCat, meta]) => {
        const item = this.menuItems.find((i) => i.category === apiCat)
        let label = meta.defaultLabel
        let sub = meta.defaultSub
        if (item) {
          label = item.name
          // Ajouter l'option végétarienne en sous-label du plat
          if (apiCat === "plat" && vegeItem) {
            sub = `🌿 ${vegeItem.name}`
          } else {
            sub = meta.defaultLabel
          }
        }
        result.push({ key: meta.key, emoji: meta.emoji, label, sub })
      })
      return result
    },
    childVoteSummary() {
      const dishTotals = {}
      this.dishCategories.forEach((d) => {
        dishTotals[d.key] = 0
      })
      const base = {
        liked: 0,
        medium: 0,
        disliked: 0,
        enoughYes: 0,
        enoughNo: 0,
        reasons: { quantity: 0, taste: 0, other: 0 },
        dishTotals,
      }
      this.childVotes.forEach((v) => {
        if (v.mealRating) base[v.mealRating]++
        if (v.enough === "yes") base.enoughYes++
        if (v.enough === "no") base.enoughNo++
        if (v.reason) base.reasons[v.reason]++
        if (v.problematicDishes) {
          v.problematicDishes.forEach((dk) => {
            if (base.dishTotals[dk] !== undefined) base.dishTotals[dk]++
          })
        }
      })
      return base
    },
  },

  async mounted() {
    // Le chargement des cantines/services est délégué à DashboardContextHeader
    // onContextChange sera appelé automatiquement au montage du composant enfant
  },

  beforeDestroy() {
    // Nettoyer l'interval avant destruction du composant
    if (this.statsInterval) {
      clearInterval(this.statsInterval)
    }
    // Sécurité : ne jamais laisser le mode borne (Vuex global) actif après destruction
    if (this.$store.state.kioskActive) {
      this.$store.commit("SET_KIOSK_ACTIVE", false)
      this.exitFullscreen()
    }
  },

  methods: {
    // ── API methods ───────────────────────────────────
    onContextChange({
      canteenId,
      selectedDate,
      selectedService,
      availableServices,
      effectifVariant,
      featureCommunications,
      featureWaste,
      featureVotes,
      featureSupervisorFeedback,
      featureSurplus,
      hasKioskExitPin,
    }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
      this.availableServices = availableServices || []
      this.effectifVariant = effectifVariant || "SIMPLE"
      this.featureCommunications = featureCommunications ?? true
      this.featureWaste = featureWaste ?? true
      this.featureVotes = featureVotes ?? true
      this.featureSupervisorFeedback = featureSupervisorFeedback ?? true
      this.featureSurplus = featureSurplus ?? true
      this.hasKioskExitPin = hasKioskExitPin ?? false
      if (canteenId && selectedService) {
        this.refreshStats()
        if (!this.statsInterval && this.currentView === "supervisor") {
          this.statsInterval = setInterval(() => this.refreshStats(), 30000)
        }
      }
      if (canteenId && selectedDate) {
        this.loadMenuItems(canteenId, selectedDate)
      }
    },

    async loadMenuItems(canteenId, date) {
      // Réinitialiser systématiquement avant chaque chargement
      this.dishes = []
      this.menuItems = []
      this.supervisorFeedbackId = null
      try {
        const resp = await fetch(`/api/v1/canteen/${canteenId}/menu-items/?date=${date}`, {
          credentials: "same-origin",
        })
        this.menuItems = resp.ok ? await resp.json() : []
        const catOrder = ["entree", "plat", "accompagnement", "produit_laitier", "dessert", "sans_viande"]
        const catLabels = {
          entree: "Entrée",
          plat: "Plat principal",
          accompagnement: "Accompagnement",
          produit_laitier: "Produit laitier",
          dessert: "Dessert",
          sans_viande: "Sans viande",
        }
        if (this.menuItems.length > 0) {
          this.dishes = catOrder
            .filter((cat) => this.menuItems.some((i) => i.category === cat))
            .map((cat) => {
              const item = this.menuItems.find((i) => i.category === cat)
              return { name: item.name, category: catLabels[cat] || cat, rating: null, comment: "" }
            })
        } else {
          // Aucun menu importé : catégories génériques pour permettre la notation
          this.dishes = ["entree", "plat", "accompagnement", "dessert"].map((cat) => ({
            name: catLabels[cat],
            category: catLabels[cat],
            rating: null,
            comment: "",
          }))
        }
      } catch {
        this.menuItems = []
      }
    },

    async handleApiError(error, operation = "opération") {
      console.error(`Erreur lors de ${operation}:`, error)

      // Gestion spécifique des erreurs courantes
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        console.warn(`Problème de réseau pour ${operation}, fonctionnement hors ligne`)
        return
      }

      if (error.status === 403) {
        console.warn(`Accès refusé pour ${operation}`)
        return
      }

      if (error.status === 400) {
        console.warn(`Données invalides pour ${operation}`)
        return
      }

      // Log générique pour autres erreurs
      console.warn(`Erreur inconnue pour ${operation}, fonctionnement local`)
    },

    // ── Date and Service Methods ──────────────────────────
    formatDisplayDate() {
      if (!this.selectedDate) return new Date().toLocaleDateString("fr-FR")
      return new Date(this.selectedDate).toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    },

    getServiceLabel() {
      const service = this.availableServices.find((s) => s.id === this.selectedService)
      if (service) {
        // Utiliser les noms de champs corrects de l'API
        const startTime = service.startTime.substring(0, 5) // HH:MM
        const endTime = service.endTime.substring(0, 5) // HH:MM
        return `${service.name} (${startTime}-${endTime})`
      }
      return "Service inconnu"
    },
    // ── Supervisor methods ───────────────────────────
    getRatingLabel(rating) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return rating ? labels[rating - 1] : "Non évalué"
    },
    getRatingLabelByIndex(idx) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return labels[idx]
    },
    updateDishRating(idx, rating) {
      this.dishes[idx].rating = rating
    },
    editDishComment(idx) {
      const c = prompt("Commentaire pour " + this.dishes[idx].name + ":", this.dishes[idx].comment || "")
      if (c !== null) this.dishes[idx].comment = c
    },

    // ── Supervisor Feedback API helpers ──────────────
    async saveSupervisorFeedback(fields) {
      if (!this.canteenId || !this.selectedService) return null
      const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] || ""
      const headers = { "X-CSRFToken": csrfToken }
      let url = `${this.apiBaseUrl}/canteen/${this.canteenId}/supervisor-feedback/`
      let method = "POST"
      const body = {
        ...fields,
        meal_date: this.selectedDate,
        service: this.selectedService,
      }
      if (this.supervisorFeedbackId) {
        url = `${this.apiBaseUrl}/canteen/${this.canteenId}/supervisor-feedback/${this.supervisorFeedbackId}/`
        method = "PATCH"
      }
      try {
        this.isSavingFeedback = true
        const resp = await fetch(url, {
          method,
          credentials: "same-origin",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        })
        if (resp.ok) {
          const data = await resp.json()
          this.supervisorFeedbackId = data.id
          return data
        }
        // 409 = doublon unique_together → récupérer l'existant
        if (resp.status === 409) {
          const existing = await fetch(
            `${this.apiBaseUrl}/canteen/${this.canteenId}/supervisor-feedback/?date=${this.selectedDate}&service_id=${this.selectedService}`,
            { credentials: "same-origin" }
          )
          if (existing.ok) {
            const list = await existing.json()
            if (list.length > 0) {
              this.supervisorFeedbackId = list[0].id
              return await this.saveSupervisorFeedback(fields)
            }
          }
        }
      } catch (err) {
        console.error("Erreur sauvegarde feedback superviseur:", err)
      } finally {
        this.isSavingFeedback = false
      }
      return null
    },

    async saveGlobalRating() {
      await this.saveSupervisorFeedback({ global_rating: this.globalRating })
      this.showGlobalFeedback = false
    },

    async saveContext() {
      await this.saveSupervisorFeedback({
        context_notes: {
          noise: this.noiseLevel,
          temperature: this.temperature,
          weather: this.weather,
        },
      })
      this.showContextForm = false
    },

    async saveDishRatings() {
      const dishRatings = {}
      const DISH_MAP = {
        starter: "entree",
        main: "plat",
        side: "accompagnement",
        dairy: "produit_laitier",
        dessert: "dessert",
      }
      this.dishCategories.forEach((cat) => {
        const dish = this.dishes.find((d) => d.name === cat.label)
        if (dish && dish.rating !== null) {
          dishRatings[DISH_MAP[cat.key] || cat.key] = dish.rating
        }
      })
      await this.saveSupervisorFeedback({ dish_ratings: dishRatings })
      this.showDetailedFeedback = false
    },

    async addComment() {
      const c = prompt("Votre commentaire :")
      if (c !== null && c.trim()) {
        this.supervisorComment = c
        await this.saveSupervisorFeedback({ comment: c })
      }
    },

    async takePhoto() {
      const input = document.createElement("input")
      input.type = "file"
      input.accept = "image/*"
      input.onchange = async (e) => {
        const file = e.target.files[0]
        if (!file || !this.canteenId || !this.selectedService) return
        if (!this.supervisorFeedbackId) {
          await this.saveSupervisorFeedback({})
        }
        const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] || ""
        const formData = new FormData()
        formData.append("photo", file)
        try {
          const resp = await fetch(
            `${this.apiBaseUrl}/canteen/${this.canteenId}/supervisor-feedback/${this.supervisorFeedbackId}/`,
            {
              method: "PATCH",
              credentials: "same-origin",
              headers: { "X-CSRFToken": csrfToken },
              body: formData,
            }
          )
          if (resp.ok) {
            this.supervisorPhotoFile = file.name
          }
        } catch (err) {
          console.error("Erreur upload photo:", err)
        }
      }
      input.click()
    },

    // ── Percentage helpers ───────────────────────────
    pct(n) {
      if (!this.childVotes.length) return 0
      return Math.round((n / this.childVotes.length) * 100)
    },
    pctOf(n, total) {
      if (!total) return 0
      return Math.round((n / total) * 100)
    },

    // ── Kiosk methods (MVP 5 & 6) ────────────────────
    // Flow: 0→1→2 → si Q2=non: 3→4→5 | si Q1=moyen/non & Q2=oui: 4→5 | sinon: 5
    startVote() {
      this.currentVote = { mealRating: null, enough: null, reason: null, problematicDishes: [] }
      this.kioskStep = 1
    },
    selectMealRating(val) {
      this.currentVote.mealRating = val
      this.kioskStep = 2
    },
    selectEnough(val) {
      this.currentVote.enough = val
      if (val === "no") {
        this.kioskStep = 3
      } else if (this.currentVote.mealRating !== "liked") {
        // Réponse positive à "assez mangé" mais repas jugé moyen/négatif → demander le plat
        this.kioskStep = 4
      } else {
        this.submitVote()
      }
    },
    selectReason(key) {
      this.currentVote.reason = key
      this.kioskStep = 4
    },
    toggleDish(key) {
      const idx = this.currentVote.problematicDishes.indexOf(key)
      if (idx === -1) {
        this.currentVote.problematicDishes.push(key)
      } else {
        this.currentVote.problematicDishes.splice(idx, 1)
      }
    },
    validateDishes() {
      this.submitVote()
    },
    getDishLabel(key) {
      const d = this.dishCategories.find((c) => c.key === key)
      return d ? d.label : key
    },
    async submitVote() {
      // Validation préliminaire
      if (!this.canteenId) {
        console.error("Impossible de soumettre le vote: ID de cantine manquant")
        this.saveVoteLocally(this.prepareVoteData())
        this.kioskStep = 5
        return
      }

      if (!this.selectedService) {
        console.error("Impossible de soumettre le vote: Service non sélectionné")
        this.saveVoteLocally(this.prepareVoteData())
        this.kioskStep = 5
        return
      }

      // Préparer les données pour l'API
      const voteData = this.prepareVoteData()

      try {
        // Appel API pour sauvegarder le vote
        const response = await this.saveVoteToAPI(voteData)

        if (response && response.ok) {
          console.log("Vote sauvegardé avec succès")
          // Rafraîchir les statistiques
          await this.refreshStats()
        } else {
          throw new Error("Réponse API invalide")
        }
      } catch (error) {
        console.error("Erreur lors de la sauvegarde du vote")
        await this.handleApiError(error, "la sauvegarde du vote")
        this.saveVoteLocally(voteData)
      }

      // Afficher l'écran de confirmation
      this.kioskStep = 5
      setTimeout(() => {
        if (this.kioskStep === 5) this.resetKiosk()
      }, 4000)
    },

    prepareVoteData() {
      return {
        meal_rating: this.currentVote.mealRating,
        enough: this.currentVote.enough,
        not_enough_reason: this.currentVote.reason,
        problematic_dishes: [...this.currentVote.problematicDishes],
        service: this.selectedService, // Le serializer attend 'service', pas 'service_id'
        meal_date: this.selectedDate,
      }
    },

    async saveVoteToAPI(voteData) {
      const csrfToken = this.getCSRFToken()

      const headers = {
        "Content-Type": "application/json",
        ...(csrfToken && { "X-CSRFToken": csrfToken }),
      }

      const response = await fetch(`${this.apiBaseUrl}/canteen/${this.canteenId}/child-votes/`, {
        method: "POST",
        headers,
        body: JSON.stringify(voteData),
        credentials: "same-origin",
      })

      if (!response.ok) {
        console.error("Erreur lors de la sauvegarde du vote:", response.status)
        throw new Error(`HTTP ${response.status}: Erreur de sauvegarde`)
      }

      return response
    },

    saveVoteLocally(voteData) {
      // Fallback: sauvegarder en local si API indisponible
      this.childVotes.push({
        mealRating: voteData.meal_rating,
        enough: voteData.enough,
        reason: voteData.not_enough_reason,
        problematicDishes: voteData.problematic_dishes,
        ts: Date.now(),
      })
    },

    async refreshStats() {
      if (this.isLoadingStats || !this.canteenId || !this.selectedService) return

      this.isLoadingStats = true
      try {
        // Utiliser la date et le service sélectionnés
        const params = new URLSearchParams({
          date: this.selectedDate,
        })

        // Ajouter service_id si un service est sélectionné
        if (this.selectedService) {
          params.append("service_id", this.selectedService)
        }

        const response = await fetch(`${this.apiBaseUrl}/canteen/${this.canteenId}/child-votes-stats/?${params}`, {
          credentials: "same-origin",
        })

        if (response.ok) {
          const stats = await response.json()

          // Mettre à jour childVotes avec les données de l'API si disponibles
          if (stats.votes && Array.isArray(stats.votes)) {
            this.childVotes = stats.votes.map((vote) => ({
              mealRating: vote.mealRating, // Backend déjà formaté en camelCase
              enough: vote.enough,
              reason: vote.reason, // Backend déjà formaté
              problematicDishes: vote.problematicDishes || [],
              ts: vote.ts, // Backend déjà en timestamp milliseconds
            }))
          } else {
            // Pas de votes pour cette date/service
            this.childVotes = []
          }

          this.lastStatsUpdate = Date.now()
          console.log("Stats rafraîchies:", `${this.childVotes.length} votes chargés`)
        } else {
          throw new Error(`Réponse HTTP ${response.status}`)
        }
      } catch (error) {
        await this.handleApiError(error, "le rafraîchissement des statistiques")
        // En cas d'erreur, garder les données locales existantes
      } finally {
        this.isLoadingStats = false
      }
    },

    getCurrentService() {
      // Déterminer le service selon l'heure actuelle
      const hour = new Date().getHours()

      if (this.availableServices.length > 0) {
        // Utiliser les services réels si disponibles
        const currentService = this.availableServices.find((s) => {
          const startHour = parseInt(s.startTime.split(":")[0])
          const endHour = parseInt(s.endTime.split(":")[0])
          return hour >= startHour && hour <= endHour
        })
        return currentService ? currentService.id : this.availableServices[0].id
      }

      // Fallback pour les services par défaut
      if (hour < 10) return "breakfast"
      if (hour < 14) return "lunch"
      if (hour < 17) return "snack"
      return "dinner"
    },

    getCSRFToken() {
      // Récupérer le token CSRF depuis les cookies ou meta tags
      const meta = document.querySelector('meta[name="csrf-token"]')
      if (meta) return meta.getAttribute("content")

      const cookies = document.cookie.split(";")
      for (let cookie of cookies) {
        if (cookie.trim().startsWith("csrftoken=")) {
          return cookie.split("=")[1]
        }
      }
      return null
    },
    resetKiosk() {
      this.kioskStep = 0
      this.currentVote = { mealRating: null, enough: null, reason: null, problematicDishes: [] }
    },

    // ── Mode borne : entrée / sortie protégée ────────────
    enterKiosk() {
      this.currentView = "kiosk"
      this.$store.commit("SET_KIOSK_ACTIVE", true)
      // Le polling de stats superviseur n'a pas de sens en mode borne
      if (this.statsInterval) {
        clearInterval(this.statsInterval)
        this.statsInterval = null
      }
      this.resetKiosk()
      // Plein écran : nécessite un geste utilisateur (le tap sur la carte le fournit)
      this.requestFullscreen()
    },
    requestFullscreen() {
      const el = document.documentElement
      const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen
      if (fn) {
        try {
          const p = fn.call(el)
          if (p && typeof p.catch === "function") p.catch(() => {})
        } catch (e) {
          /* plein écran refusé (hors geste utilisateur / non supporté) : on l'ignore */
        }
      }
    },
    exitFullscreen() {
      const fn = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen
      if (fn && (document.fullscreenElement || document.webkitFullscreenElement)) {
        try {
          const p = fn.call(document)
          if (p && typeof p.catch === "function") p.catch(() => {})
        } catch (e) {
          /* rien à faire */
        }
      }
    },
    requestKioskExit() {
      // Aucun PIN configuré : sortie libre (comportement historique)
      if (!this.hasKioskExitPin) {
        this.exitKiosk()
        return
      }
      this.kioskPinInput = ""
      this.kioskPinError = false
      this.showKioskExitDialog = true
      this.$nextTick(() => {
        if (this.$refs.kioskPinField) this.$refs.kioskPinField.focus()
      })
    },
    cancelKioskExit() {
      this.showKioskExitDialog = false
      this.kioskPinInput = ""
      this.kioskPinError = false
    },
    async submitKioskPin() {
      if (this.kioskPinChecking || !this.canteenId) return
      this.kioskPinChecking = true
      this.kioskPinError = false
      try {
        const resp = await fetch(`${this.apiBaseUrl}/canteen/${this.canteenId}/verify-kiosk-pin/`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCSRFToken() },
          body: JSON.stringify({ pin: this.kioskPinInput }),
        })
        const data = resp.ok ? await resp.json() : { valid: false }
        if (data.valid) {
          this.showKioskExitDialog = false
          this.exitKiosk()
        } else {
          this.kioskPinError = true
          this.kioskPinInput = ""
          this.$nextTick(() => {
            if (this.$refs.kioskPinField) this.$refs.kioskPinField.focus()
          })
        }
      } catch (e) {
        this.kioskPinError = true
      } finally {
        this.kioskPinChecking = false
      }
    },
    exitKiosk() {
      this.currentView = "supervisor"
      this.$store.commit("SET_KIOSK_ACTIVE", false)
      this.showKioskExitDialog = false
      this.kioskPinInput = ""
      this.kioskPinError = false
      this.resetKiosk()
      this.exitFullscreen()
      // Relance le rafraîchissement des stats côté superviseur
      if (this.canteenId && this.selectedService) this.refreshStats()
    },
  },
}
</script>

<style scoped>
/* ══════════════════════════════════════════════════════
   ROOT & VIEW SWITCHER
══════════════════════════════════════════════════════ */
.app-root {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  min-height: 100vh;
  background: #f8f9fa;
  /* En mode borne, le parent v-container passe en flex (fill-height) : sans largeur
     explicite, ce conteneur se rétrécit à son contenu. On force la pleine largeur. */
  width: 100%;
  flex: 1 1 auto;
}

.view-switcher {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem 0;
  background: #fff;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
}

.view-tab {
  padding: 0.6rem 1.25rem;
  border: 2px solid transparent;
  border-radius: 8px 8px 0 0;
  background: #f3f4f6;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
}
.view-tab.active {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}

/* ══════════════════════════════════════════════════════
   SUPERVISOR DASHBOARD
══════════════════════════════════════════════════════ */
.supervisor-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}
.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}
.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
}

/* Context selectors */
.context-selectors {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 1.5rem 0;
  flex-wrap: wrap;
}

.selector-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.selector-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.date-selector,
.service-selector,
.canteen-selector {
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  background: white;
  color: #1a1a1a;
  transition: all 0.2s;
  min-width: 180px;
}

.date-selector:focus,
.service-selector:focus,
.canteen-selector:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.service-selector:disabled,
.canteen-selector:disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.service-selector,
.canteen-selector {
  cursor: pointer;
}

.service-debug {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #f59e0b;
  font-weight: 500;
}

/* No votes message */
.no-votes-message {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: #f3f4f6;
  border: 1.5px solid #d1d5db;
  border-radius: 999px;
  padding: 0.6rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  margin-top: 1rem;
}

.no-votes-icon {
  font-size: 1.1rem;
}

.no-votes-text {
  font-style: italic;
}

/* Vote summary bar */
.vote-summary-bar {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  background: #f0fdf4;
  border: 1.5px solid #86efac;
  border-radius: 999px;
  padding: 0.4rem 1.2rem;
  font-size: 0.82rem;
  font-weight: 600;
  margin-top: 0.5rem;
}
.vote-summary-label {
  color: #166534;
}
.vote-pill {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
}
.pill-green {
  background: #d1fae5;
  color: #065f46;
}
.pill-orange {
  background: #fed7aa;
  color: #9a3412;
}
.pill-red {
  background: #fee2e2;
  color: #991b1b;
}
.pill-blue {
  background: #dbeafe;
  color: #1e40af;
}

/* Cards grid */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}
.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
}
.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.card-featured {
  border: 2px solid #f97316;
  background: linear-gradient(135deg, #fff7ed 0%, white 100%);
}
.card-highlight {
  border: 2px solid #8b5cf6;
  background: linear-gradient(135deg, #faf5ff 0%, white 100%);
}
.card-votes {
  border: 2px solid #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, white 100%);
}
.card-kiosk {
  border: 2px solid #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, white 100%);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}
.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}
.card-link {
  display: inline-flex;
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.action-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}
.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.action-btn:hover {
  background: #eff6ff;
  border-color: #2563eb;
}
.action-icon {
  font-size: 2rem;
}

/* Mini bars in vote card */
.vote-mini-bars {
  margin: 0.75rem 0;
}
.mini-bar-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  font-size: 1.1rem;
}
.mini-bar-track {
  flex: 1;
  background: #f3f4f6;
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
}
.mini-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.5s ease;
}
.fill-green {
  background: #10b981;
}
.fill-orange {
  background: #f59e0b;
}
.fill-red {
  background: #ef4444;
}
.fill-blue {
  background: #3b82f6;
}
.mini-bar-val {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 2.5rem;
  text-align: right;
}

/* Badges */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
}
.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}
.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}
.badge-green {
  background: #d1fae5;
  color: #065f46;
}
.badge-purple {
  background: #ede9fe;
  color: #5b21b6;
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}
.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.modal-large {
  max-width: 700px;
}
.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}
.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}
.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Result bars (vote results modal) */
.result-section {
  margin-bottom: 1.75rem;
}
.result-bars {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.result-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.result-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  min-width: 9rem;
}
.result-track {
  flex: 1;
  background: #f3f4f6;
  border-radius: 999px;
  height: 14px;
  overflow: hidden;
}
.result-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.6s ease;
}
.result-count {
  font-size: 0.82rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 4.5rem;
  text-align: right;
}

/* Rating selector */
.rating-selector {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.rating-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.rating-btn:hover {
  background: #eff6ff;
}
.rating-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
  transform: scale(1.05);
}
.rating-emoji {
  font-size: 2.5rem;
}
.rating-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
}

/* Dishes */
.dishes-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}
.dish-card {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
}
.dish-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}
.dish-ratings {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.rating-mini-btn {
  font-size: 1.5rem;
  background: white;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}
.rating-mini-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
  transform: scale(1.1);
}
.rating-percent {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}
.dish-comment {
  font-size: 0.875rem;
  color: #f97316;
  font-style: italic;
  margin: 0.75rem 0 0 0;
}

/* Context */
.detail-section {
  margin-bottom: 1.5rem;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}
.weather-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}
.weather-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.weather-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
}
.weather-icon {
  font-size: 2.5rem;
}
.weather-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}
.temperature-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.input-field {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
}
.input-field:focus {
  outline: none;
  border-color: #2563eb;
}
.temperature-unit {
  font-size: 1.125rem;
  font-weight: 600;
  color: #6b7280;
}
.noise-selector {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.noise-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}
.noise-btn.active {
  border-color: #3b82f6;
  background: #eff6ff;
}
.noise-icon {
  font-size: 1.5rem;
}
.noise-label {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Messages */
.messages-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.message-card {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}
.message-absence {
  background: #fef2f2;
  border-color: #ef4444;
}
.message-info {
  background: #eff6ff;
  border-color: #2563eb;
}
.message-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.message-source {
  font-size: 0.75rem;
  color: #6b7280;
}
.message-content {
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
}

/* Fullscreen */
.fullscreen-modal {
  position: fixed;
  inset: 0;
  background: white;
  z-index: 2000;
  overflow-y: auto;
  padding: 1rem;
}
.fullscreen-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  z-index: 2001;
}

/* Buttons */
.btn {
  display: block;
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  margin-top: 0.75rem;
  font-size: 1rem;
}
.btn-primary {
  background: #2563eb;
  color: white;
}
.btn-danger {
  background: #ef4444;
  color: white;
}

/* Menu */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}
.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}
.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

/* ══════════════════════════════════════════════════════
   KIOSK — BORNE VOTE ENFANT (MVP 5 & 6)
══════════════════════════════════════════════════════ */
.kiosk-root {
  position: relative;
  /* Mode borne = plein écran (barre superviseur masquée) : occuper toute la hauteur */
  min-height: 100vh;
  background: linear-gradient(160deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

/* Sortie de borne discrète mais toujours perceptible (icône engrenage en coin).
   Pastille sombre + glyphe blanc net + fine bordure : reste lisible sur les
   sections claires comme foncées du dégradé, sans attirer l'œil de l'enfant. */
.kiosk-exit {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  z-index: 20;
  transition: background 0.2s, color 0.2s, transform 0.2s;
}
.kiosk-exit:hover,
.kiosk-exit:focus-visible {
  background: rgba(0, 0, 0, 0.55);
  color: white;
  transform: scale(1.08);
}

/* Dialogue PIN de sortie */
.kiosk-pin-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}
.kiosk-pin-dialog {
  background: white;
  color: #1f2937;
  border-radius: 16px;
  padding: 2rem;
  width: 90%;
  max-width: 360px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}
.kiosk-pin-title {
  margin: 0 0 0.5rem;
  font-size: 1.35rem;
  font-weight: 700;
}
.kiosk-pin-sub {
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: #6b7280;
}
.kiosk-pin-input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1.5rem;
  letter-spacing: 0.4em;
  text-align: center;
  border: 2px solid #d1d5db;
  border-radius: 10px;
  outline: none;
  box-sizing: border-box;
}
.kiosk-pin-input:focus {
  border-color: #2563eb;
}
.kiosk-pin-input--error {
  border-color: #dc2626;
}
.kiosk-pin-error {
  margin: 0.75rem 0 0;
  color: #dc2626;
  font-size: 0.85rem;
  font-weight: 600;
}
.kiosk-pin-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
}
.kiosk-pin-cancel,
.kiosk-pin-submit {
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  border: none;
}
.kiosk-pin-cancel {
  background: #f3f4f6;
  color: #374151;
}
.kiosk-pin-submit {
  background: #2563eb;
  color: white;
}
.kiosk-pin-submit:disabled {
  opacity: 0.6;
  cursor: default;
}

.kiosk-screen {
  width: 100%;
  max-width: 480px;
  text-align: center;
}

/* Welcome */
.kiosk-welcome {
}
.kiosk-mascot {
  font-size: 6rem;
  margin-bottom: 1rem;
  animation: bounce 1.2s infinite;
}
@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-12px);
  }
}
.kiosk-welcome-title {
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.kiosk-welcome-sub {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 2.5rem 0;
}
.kiosk-start-btn {
  background: white;
  color: #764ba2;
  border: none;
  padding: 1.25rem 3rem;
  border-radius: 999px;
  font-size: 1.5rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s;
}
.kiosk-start-btn:active {
  transform: scale(0.97);
}

/* Progress dots */
.kiosk-progress {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
}
.kiosk-progress-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  transition: background 0.3s;
}
.kiosk-progress-dot.active {
  background: white;
}

/* Question */
.kiosk-question {
  font-size: 2.2rem;
  font-weight: 800;
  color: white;
  line-height: 1.2;
  margin: 0 0 2.5rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Choice buttons */
.kiosk-choices {
  display: flex;
  justify-content: center;
  gap: 1.25rem;
}
.kiosk-choices-3 {
  flex-direction: row;
  flex-wrap: wrap;
}
.kiosk-choices-2 {
  flex-direction: row;
}

.kiosk-choice-btn {
  flex: 1;
  min-width: 110px;
  max-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}
.kiosk-choice-btn:active {
  transform: scale(0.94);
}

.choice-green {
  background: #d1fae5;
}
.choice-orange {
  background: #fed7aa;
}
.choice-red {
  background: #fee2e2;
}

.kiosk-smiley {
  font-size: 4rem;
}
.kiosk-choice-label {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1a1a1a;
}

/* Reason buttons (Q3 conditionnelle MVP 6) */
.kiosk-choices-col {
  flex-direction: column;
  gap: 1rem;
  align-items: stretch;
  max-width: 400px;
  margin: 0 auto;
}
.kiosk-reason-btn {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 1.75rem;
  background: white;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transition: transform 0.12s, box-shadow 0.12s;
}
.kiosk-reason-btn:active {
  transform: scale(0.97);
}
.kiosk-reason-icon {
  font-size: 2.2rem;
}
.kiosk-reason-label {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: left;
}

/* Confirmation */
.kiosk-confirm {
}
.kiosk-confirm-icon {
  font-size: 7rem;
  animation: pop 0.4s cubic-bezier(0.17, 0.67, 0.42, 1.35);
}
@keyframes pop {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.kiosk-confirm-title {
  font-size: 3.5rem;
  font-weight: 900;
  color: white;
  margin: 0.5rem 0;
}
.kiosk-confirm-sub {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 1.5rem 0;
}
.kiosk-confirm-recap {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 16px;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  line-height: 2;
  margin-bottom: 2rem;
}
.kiosk-next-btn {
  background: white;
  color: #764ba2;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 999px;
  font-size: 1.25rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter {
  transform: translateX(40px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateX(-40px);
  opacity: 0;
}

/* ── Dish selector (étape 4) ─────────────────────── */
.kiosk-question-sm {
  font-size: 1.8rem;
}

.kiosk-dish-hint {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  margin: -1rem 0 1.5rem 0;
}

.kiosk-dish-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  max-width: 420px;
  margin: 0 auto 1.75rem auto;
}

.kiosk-dish-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 1.25rem 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;
}
.kiosk-dish-btn:active {
  transform: scale(0.95);
}
.kiosk-dish-btn.dish-selected {
  background: rgba(255, 255, 255, 0.92);
  border-color: white;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.35);
}

.kiosk-dish-plate {
  position: relative;
  display: inline-block;
}
.kiosk-dish-emoji {
  font-size: 3.5rem;
  display: block;
}

.dish-check {
  position: absolute;
  top: -6px;
  right: -10px;
  background: #10b981;
  color: white;
  font-size: 0.85rem;
  font-weight: 900;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2.5px solid white;
  animation: pop-check 0.2s cubic-bezier(0.17, 0.67, 0.42, 1.35);
}
@keyframes pop-check {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

.kiosk-dish-name {
  font-size: 1rem;
  font-weight: 800;
  color: white;
}
.kiosk-dish-btn.dish-selected .kiosk-dish-name {
  color: #1a1a1a;
}

.kiosk-dish-sub {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}
.kiosk-dish-btn.dish-selected .kiosk-dish-sub {
  color: #6b7280;
}

.kiosk-validate-btn {
  background: rgba(255, 255, 255, 0.22);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.45);
  padding: 1rem 2.5rem;
  border-radius: 999px;
  font-size: 1.15rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 200px;
}
.kiosk-validate-btn.kiosk-validate-ready {
  background: white;
  color: #764ba2;
  border-color: white;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}
.kiosk-validate-btn:active {
  transform: scale(0.97);
}

/* ── Responsive ─────────────────────────────────── */
@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
  .rating-selector {
    grid-template-columns: repeat(3, 1fr);
  }
  .kiosk-question {
    font-size: 1.75rem;
  }
  .kiosk-choice-btn {
    padding: 1.25rem 0.75rem;
  }
  .kiosk-smiley {
    font-size: 3rem;
  }
}
</style>
